import { ReasonCandidateSuggestion } from '@modules/common/components/ReasonCandidateSuggestion';
import { ReasonCandidate } from '@modules/common/components/ReasonCandidateSuggestion/types';
import { useSearchUnassignedRCs } from '@modules/common/hooks';
import { RuleRounded } from '@mui/icons-material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  GetRcSuggestionQuery,
  SearchUnassignedRCsQuery,
  useGetRcSuggestionQuery,
} from 'generated/graphql';
import { SyntheticEvent, useCallback, useRef, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';

const RC_SUGGESTIONS_LIMIT = 2;
const NO_DEFINITIONS_FOUND = 'No Definitions found';

interface ReasonDefinitionAutocompleteListProps {
  reasonRCCount?: number;
  reasonName: string;
  selectedReasonRCs: ReasonCandidate[];
  onSelectedReasonRCChange: (selectedReasonRC: ReasonCandidate) => void;
  renderReasonCandidateList: () => React.ReactNode;
  renderSecondaryActionCTA?: () => React.ReactNode;
}

export const ReasonDefinitionAutocompleteList = ({
  reasonRCCount,
  reasonName,
  selectedReasonRCs,
  onSelectedReasonRCChange,
  renderReasonCandidateList,
  renderSecondaryActionCTA,
}: ReasonDefinitionAutocompleteListProps) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const [searchTerm, setSearchTerm] = useState('');
  const suggestionRef = useRef<HTMLDivElement>(null);

  const selectedReasonCandidatesIDs = selectedReasonRCs.map((rc) => rc.id);
  const selectedReasonCandidatesName = selectedReasonRCs.map(
    (rc) => rc.displayName,
  );

  const getReasonCandidateSuggestions = useCallback(
    (data?: GetRcSuggestionQuery) => {
      if (!reasonName.trim()) {
        return [];
      }

      return (
        data?.getRCSuggestion?.suggestedRCs
          ?.filter((reasonCandidate) => {
            const isAlreadySelectedRC =
              selectedReasonCandidatesIDs.includes(reasonCandidate.id) ||
              selectedReasonCandidatesName.includes(reasonCandidate.name);

            return !isAlreadySelectedRC;
          })
          .map(
            (reasonCandidate) =>
              ({
                id: reasonCandidate.id,
                displayName: reasonCandidate.name,
                recordIDs: reasonCandidate.recordIDs,
              } as ReasonCandidate),
          ) || []
      );
    },
    [reasonName, selectedReasonCandidatesIDs, selectedReasonCandidatesName],
  );

  const { data: reasonCandidateSuggestionsResponse } = useGetRcSuggestionQuery({
    variables: {
      request: {
        tenantID,
        reasonName,
        limit: RC_SUGGESTIONS_LIMIT,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const reasonCandidateSuggestions = getReasonCandidateSuggestions(
    reasonCandidateSuggestionsResponse,
  );

  const {
    data: searchUnassignedRCsResponse,
    loading: areUnassignedRCsLoading,
  } = useSearchUnassignedRCs({
    searchTerm: searchTerm.trim(),
  });

  const getOptions = useCallback(
    (data?: SearchUnassignedRCsQuery): ReasonCandidate[] => {
      if (!searchTerm.trim()) {
        return [];
      }

      const searchResults: ReasonCandidate[] =
        data?.searchUnassignedRCs?.unassignedRCs
          ?.filter((reasonCandidate) => {
            const isAlreadySelectedRC =
              selectedReasonCandidatesIDs.includes(reasonCandidate.id) ||
              selectedReasonCandidatesName.includes(reasonCandidate.name);

            return !isAlreadySelectedRC;
          })
          .map((reasonCandidate) => ({
            id: reasonCandidate.id,
            displayName: reasonCandidate.name,
            recordIDs: reasonCandidate.recordIDs,
          })) || [];

      return !areUnassignedRCsLoading && searchResults.length === 0
        ? [
            {
              id: NO_DEFINITIONS_FOUND,
              displayName: `"${searchTerm.trim()}" doesn't correlates to any existing Definitions. Please Search for Definitions 
            describing or relating to the Reason`,
              recordIDs: [],
            },
          ]
        : searchResults;
    },
    [
      areUnassignedRCsLoading,
      searchTerm,
      selectedReasonCandidatesIDs,
      selectedReasonCandidatesName,
    ],
  );

  const options = getOptions(searchUnassignedRCsResponse);

  const onSearchTermChange = (
    _: SyntheticEvent<Element, Event>,
    reasonCandidate: string | ReasonCandidate | null,
  ) => {
    if (
      !reasonCandidate ||
      typeof reasonCandidate === 'string' ||
      !searchTerm.trim()
    ) {
      return;
    }

    onSelectedReasonRCChange(reasonCandidate);
    setSearchTerm('');
  };

  const onRCSuggestionClick = (reasonCandidate: ReasonCandidate) => {
    onSelectedReasonRCChange(reasonCandidate);
  };

  const renderAutocompleteOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: ReasonCandidate,
  ) => {
    return (
      <MenuItem
        {...props}
        sx={{
          justifyContent: 'space-between !important',
        }}
      >
        <Typography whiteSpace="normal">{option.displayName}</Typography>
      </MenuItem>
    );
  };

  const renderAutocompleteInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      autoFocus
      fullWidth
      size="small"
      placeholder="Search for Definitions describing or relating to the Reason"
    />
  );

  const definitionText =
    reasonRCCount ?? selectedReasonRCs.length > 1
      ? 'Definitions'
      : 'Definition';

  return (
    <Stack spacing={1} flex={1} direction="row">
      {!reasonRCCount ? (
        <RuleRounded
          sx={{
            color: 'text.disabled',
          }}
        />
      ) : null}
      <Stack spacing={1} flex={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1">
            {definitionText} ({reasonRCCount ?? selectedReasonRCs.length})
          </Typography>
          {renderSecondaryActionCTA?.()}
        </Stack>
        <Autocomplete
          open
          autoComplete
          disablePortal
          disableClearable={!searchTerm.trim()}
          value={searchTerm}
          options={options}
          getOptionDisabled={(option) => option.id === NO_DEFINITIONS_FOUND}
          loading={areUnassignedRCsLoading}
          // Hide `No Options` option on search-term absence
          freeSolo
          getOptionLabel={(option) => {
            return typeof option === 'string' ? option : option.displayName;
          }}
          renderOption={renderAutocompleteOption}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          onChange={onSearchTermChange}
          onInputChange={(_, value) => {
            setSearchTerm(value);
          }}
          renderInput={renderAutocompleteInput}
          componentsProps={{ paper: { elevation: 8 } }}
          filterOptions={(option) => option}
        />
        <Stack
          ref={suggestionRef}
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          {reasonCandidateSuggestions.length ? (
            <AutoAwesomeRoundedIcon
              fontSize="small"
              sx={{
                color: 'text.secondary',
              }}
            />
          ) : null}
          {reasonCandidateSuggestions?.map((reasonCandidate) => (
            <ReasonCandidateSuggestion
              key={reasonCandidate.id}
              reasonCandidate={reasonCandidate}
              onClick={onRCSuggestionClick}
            />
          ))}
        </Stack>
        {renderReasonCandidateList()}
      </Stack>
    </Stack>
  );
};
